import { useMutation } from '@apollo/client';
import { useToast } from '@synoptic/ui-kit/toast/toast-provider.js';
import { gql } from '@/__generated__';
import { ListFormInputs } from '@/components/stream-deck/list-form/list-form';
import { CustomCollectionFragment } from './fragments';

const ADD_CUSTOM_COLLECTION = gql(`
  mutation AddCustomCollection($input: AddCustomCollectionInput!) {
    addCustomCollection(input: $input) {
      ...CustomCollectionFragment
    }
  }
`);

export const useAddCustomCollection = (shouldNotifyOnSuccess = false) => {
  const toast = useToast();

  const [addCustomCollectionMutation, { loading }] = useMutation(
    ADD_CUSTOM_COLLECTION,
    {
      refetchQueries: [],
      awaitRefetchQueries: false,
      update(cache, result, { variables }) {
        if (!variables?.input.streamDeckId) {
          return;
        }
        const newCollection = result.data?.addCustomCollection;
        if (newCollection) {
          const fragmentId = cache.identify(newCollection);
          cache.writeFragment({
            fragment: CustomCollectionFragment,
            fragmentName: 'CustomCollectionFragment',
            data: newCollection,
          });

          const streamDeckId = `StreamDeck:${variables.input.streamDeckId}`;
          cache.modify({
            id: streamDeckId,
            fields: {
              collections(existingCollections = []) {
                return [...existingCollections, { __ref: fragmentId }];
              },
            },
          });

          cache.evict({
            fieldName: 'aiFilters',
          });
          cache.gc();
        }
      },
      onCompleted: () => {
        if (shouldNotifyOnSuccess) {
          toast.success({
            title: 'List created successfully',
          });
        }
      },
      onError: () => {
        toast.error({
          title: 'Error while creating a new list',
          subTitle: 'Please try again later',
        });
      },
    },
  );

  const addCollection = async ({
    name,
    sources,
    sourceCustomTargets,
    streamSourceIds,
    streamExcludedPostTypes,
    filters,
    streamDeckId,
    highlights,
    matchingSourcesOnly,
  }: ListFormInputs & { streamDeckId: string }) => {
    return addCustomCollectionMutation({
      variables: {
        input: {
          name,
          streamDeckId,
          sources: sources.map((s) => ({
            streamId: s.id,
            customTargets: sourceCustomTargets?.[s.id]?.length
              ? sourceCustomTargets[s.id]
              : null,
            sourceIds: streamSourceIds?.[s.id]?.length
              ? streamSourceIds[s.id]
              : null,
            excludedPostTypes: streamExcludedPostTypes?.[s.id]?.length
              ? streamExcludedPostTypes[s.id]
              : null,
          })),
          filters: { ...filters, ai: filters.ai?.id },
          highlights,
          matchingSourcesOnly,
        },
      },
    });
  };

  return {
    addCollection,
    loading,
  };
};
